import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import {
  useAuth,
  useFlagSpaceAccessibility,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData } from "common/types";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import AnalyticsService from "@/services/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import { Storage } from "common/storage";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

type FlagSpaceAccessibilityProps = {
  reviewLevelId: string;
  businessId: string;
  spaceName: string;
  venueName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: any;
};

function FlagSpaceAccessibility({
  reviewLevelId,
  businessId,
  spaceName,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: FlagSpaceAccessibilityProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const { user } = useAuth();

  const showToast = useCustomToast();

  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isZoomedIn = useZoomDetection();

  const { mutate, error, isPending, data } = useFlagSpaceAccessibility();

  useEffect(() => {
    if (error) {
      console.log("useFlagSpaceAccessibility error", error);

      AnalyticsService.trackGAEventHandler("flagSpaceAccessibilityError");
      AnalyticsService.trackMx("flagSpaceAccessibilityError", {
        VenueId: venueDetails?.id,
        VenueBrandId: venueDetails?.brandId,
        VenueBrandName: venueDetails?.brandName,
        VenueAddress: venueDetails?.address,
        VenueName: venueDetails?.name,
        VenueTypes: venueDetails?.types,
        SpaceName: spaceName,
        ReviewLevelId: reviewLevelId,
      });

      showToast(error.message || "Failed to report issue", "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      console.log("useFlagSpaceAccessibility success", data);
      showToast(
        "Thank you for reporting this issue. We'll review it shortly.",
        "success",
      );
      setAttachedImages([]);
      setNote("");
      onClose();

      AnalyticsService.trackGAEventHandler("flagSpaceAccessibilitySuccess");
      AnalyticsService.trackMx("flagSpaceAccessibilitySuccess", {
        VenueId: venueDetails?.id,
        VenueBrandId: venueDetails?.brandId,
        VenueBrandName: venueDetails?.brandName,
        VenueAddress: venueDetails?.address,
        VenueName: venueDetails?.name,
        VenueTypes: venueDetails?.types,
        SpaceName: spaceName,
        ReviewLevelId: reviewLevelId,
        ReviewerName: user?.name,
      });
    }
  }, [data, isDark]);

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (note.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    setErrorMessage(null);

    AnalyticsService.trackGAEventHandler("flagSpaceAccessibilitySubmit");
    AnalyticsService.trackMx("flagSpaceAccessibilitySubmit", {
      VenueId: venueDetails?.id,
      VenueBrandId: venueDetails?.brandId,
      VenueBrandName: venueDetails?.brandName,
      VenueAddress: venueDetails?.address,
      VenueName: venueDetails?.name,
      VenueTypes: venueDetails?.types,
      SpaceName: spaceName,
      ReviewLevelId: reviewLevelId,
      ReviewerName: user?.name,
    });

    mutate({
      reviewLevelId,
      businessId,
      notes: note,
      images: attachedImages,
      accessToken: Storage.getCurrentUserToken(),
    });
  };

  const firstFocusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (firstFocusRef.current && isOpen) {
      firstFocusRef.current.focus();
    }
  }, [isOpen]);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <div
        style={modalStyle}
        className={cn(modalClassName)}
        aria-label="Flag Space Accessibility"
        ref={firstFocusRef}
        tabIndex={-1}
      >
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <StandardButton
            onClick={() => {
              setNote("");
              setAttachedImages([]);
              setErrorMessage(null);
              onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1 className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4" id="flag-space-modal-title">
            Report Issue
          </h1>
          <div className="flex-shrink-0 w-10"></div>
        </header>

        <div
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {venueName}
                </div>
                <h2 className="self-stretch text-fg-neutral-strong title-s-semibold break-words">
                  Report an Issue with "{spaceName}"
                </h2>
              </div>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-3">
                Have you spotted something wrong with the accessibility
                information for this space?
              </div>

              <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex mt-4">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  Describe the issue
                </label>
                <Textarea
                  id="review"
                  placeholder="Please describe what's incorrect or missing..."
                  className="scrollbar-thin h-32"
                  value={note}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="label-s-medium text-fg-neutral-subtle mt-3"
                >
                  <span className={note.length < 5 ? "text-red-600" : ""}>
                    {note.length}
                  </span>
                  /500 Characters - at least 5 characters required
                </div>

                {errorMessage && (
                  <div className="text-red-600 mt-2" role="alert">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto" tabIndex={0}>
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-full md:w-[327px] justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
              >
                <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                  Add Photos
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-full md:w-[327px] justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit report"
              >
                Submit Report
              </WideButton>
            </div>
          </div>
        </footer>
      </div>
    </Modal>
  );
}

export default FlagSpaceAccessibility;
